import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  AcYearAvailability,
  BaseQueryParams,
  IResponse,
  IResponsePaginate,
  Ppm,
  WithMetadata,
  TaskHistory,
  PpmDetails,
  PpmModeleRequest,
} from '@/types';

export interface PpmQuery extends BaseQueryParams {
  ref?: string;
  nom?: string;
  annee?: string;
  statusId?: string;
  budgetEstime?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PpmService {
  BASEURL_PPM = `${environment.api.ppmUrl}/ppms`;

  httpClient = inject(HttpClient);

  // PPMs
  getPpms(params?: PpmQuery): Observable<IResponsePaginate<Ppm>> {
    return this.httpClient.get<IResponsePaginate<Ppm>>(`${this.BASEURL_PPM}`, { params: params as HttpParams });
  }

  getPpm(id: number): Observable<WithMetadata<Ppm>> {
    return this.httpClient.get<WithMetadata<Ppm>>(`${this.BASEURL_PPM}/${id}`);
  }

  getCurrentUserPpm(params?: PpmQuery): Observable<IResponsePaginate<Ppm>> {
    return this.httpClient.get<IResponsePaginate<Ppm>>(`${this.BASEURL_PPM}/current_year/user`, { params: params as HttpParams });
  }

  getPpmHistory(id: number): Observable<IResponsePaginate<Ppm>> {
    return this.httpClient.get<IResponsePaginate<Ppm>>(`${this.BASEURL_PPM}/history/minimal/${id}`);
  }

  getPpmVersion(id: number, version?: number): Observable<WithMetadata<Ppm>> {
    return this.httpClient.get<WithMetadata<Ppm>>(`${this.BASEURL_PPM}/${id}/${version}`);
  }

  getPpmDetails(id: number): Observable<PpmDetails> {
    return this.httpClient.get<PpmDetails>(`${this.BASEURL_PPM}/details/${id}`);
  }

  startWorkflow(id: number): Observable<IResponse<Ppm>> {
    return this.httpClient.post<IResponse<Ppm>>(`${this.BASEURL_PPM}/flowable/start/${id}`, {});
  }

  getPpmTaskHistory(processInstanceId: string): Observable<TaskHistory[]> {
    return this.httpClient.get<TaskHistory[]>(`${this.BASEURL_PPM}/flowable/task-history/${processInstanceId}`);
  }

  refreshPpm(ppmId: number): Observable<IResponse<Ppm>> {
    return this.httpClient.post<IResponse<Ppm>>(`${this.BASEURL_PPM}/actualise/${ppmId}`, {});
  }

  addPpm(ppm: FormData): Observable<IResponse<Ppm>> {
    return this.httpClient.post<IResponse<Ppm>>(`${this.BASEURL_PPM}/with-relationships`, ppm);
  }

  addPpmPerAc(ppm: FormData): Observable<IResponse<Ppm>> {
    return this.httpClient.post<IResponse<Ppm>>(`${this.BASEURL_PPM}/create_per_ac`, ppm);
  }

  updatePpm(id: number, version: number, ppm: FormData): Observable<IResponse<Ppm>> {
    return this.httpClient.put<IResponse<Ppm>>(`${this.BASEURL_PPM}/${id}/${version}/with-relationships`, ppm);
  }

  deletePpm(id: number, version = 1): Observable<IResponse<Ppm>> {
    return this.httpClient.delete<IResponse<Ppm>>(`${this.BASEURL_PPM}/${id}/${version}`);
  }

  getAcWithNoPpm(): Observable<AcYearAvailability[]> {
    return this.httpClient.get<AcYearAvailability[]>(`${this.BASEURL_PPM}/current_year/user/acs_without_paa`);
  }

  downloadPpmModel(modelRequest: PpmModeleRequest): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL_PPM}/flowable/modele-ppm`, modelRequest, {
      responseType: 'blob',
    });
  }

  downloadExcelPpmModel(modelRequest: Partial<PpmModeleRequest>): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL_PPM}/flowable/modele-ppm-excel`, modelRequest, {
      responseType: 'blob',
    });
  }

  uploadPpmModel(taskId: string, ppmFormData: FormData): Observable<IResponse<PpmDetails>> {
    return this.httpClient.post<IResponse<PpmDetails>>(`${this.BASEURL_PPM}/flowable/tasks/${taskId}/validate_with_model`, ppmFormData);
  }

  validateReporterReview(taskId: string, ppmFormData: FormData): Observable<IResponse<void>> {
    return this.httpClient.post<IResponse<void>>(`${this.BASEURL_PPM}/flowable/tasks/${taskId}/validate_avis_note_rapporteur`, ppmFormData);
  }
}
